import { takeEvery, delay } from 'redux-saga'
import { call, put, take, select } from 'redux-saga/effects'
import browserHistory from 'helpers/history'

import { loginSync, fetchShareDataSync, sendFormSync, cartSync } from '../sync'
import t from './actions/actionTypes'
import binders from './sagas/binders_sagas'
import users from './sagas/user_sagas'
import cart from './sagas/cart_sagas'
import myAccount from './sagas/my_account_sagas'
import privateLibrary from './sagas/private_library_sagas'
import folders from './sagas/folders_sagas'
import onboarding from './sagas/onboarding_sagas'
import adminOnboarding from './sagas/admin_onboarding_sagas'
import themes from './sagas/themes_sagas'
import signupActions from 'redux/actions/signupActions'
import { localize } from 'helpers/translator'
import { signupSelectors, qsSelectors } from 'redux/selectors'
import mediaSearch from './sagas/media_search_sagas'
import metrics from './sagas/metrics_sagas'
import quiz from './sagas/quiz_sagas'
import matchingPairsCt from './sagas/matching_pairs_ct_sagas'
import storefront from './sagas/storefront_sagas'
import drawIt from './sagas/draw_it_sagas'
import processing from './sagas/processing_sagas'
import poll from './sagas/poll_sagas'
import oeq from './sagas/oeq_sagas'
import desmos from './sagas/desmos_sagas'
import flocabulary from './sagas/flocabulary_sagas'
import notification from './sagas/notification_sagas'
import store from './sagas/store_sagas'
import student from './sagas/student_sagas'
import dmStorageExperiment from './sagas/dm_storage_experiment_sagas'
import callAny from './sagas/helpers/call_any'
import myLibrary from './sagas/my_library_sagas'
import lti from './sagas/lti_sagas'
import game from './sagas/game_sagas'
import mixpanel from 'helpers/mixpanel'
import checkSsoLocation from 'helpers/checkSsoLocation'
import firstLesson from './sagas/first_lesson_sagas'
import googleSlidesSagas from './sagas/google_slides_sagas'
import onboardingActivitySagas from './sagas/onboarding_activity_sagas'
import immersiveReaderSagas from './sagas/immersive_reader_sagas'
import goalsSagas from './sagas/goals_sagas'
import quickLaunchSagas from './sagas/quick_launch_sagas'
import interactiveVideoSagas from './sagas/interactive_video_sagas'
import navigationMenuSagas from './sagas/navigation_menu_sagas'
import userSchoolProfileSagas from './sagas/user_school_profile_sagas'
import flipgridSagas from './sagas/flipgrid_sagas'
import interactiveVideoCreationSagas from './sagas/interactive_video_creation_sagas'
import fillInTheBlanksSession from './sagas/fillInTheBlanks/session_sagas'
import mediaSlideSagas from './sagas/media_slides_sagas'
import lessonSessionSagas from './sagas/lesson_session_sagas'
import activitiesSagas from './sagas/activities_sagas'
import teacherSagas from './sagas/teacher_sagas'
import {
	creationToolOeqSagas,
	creationToolMcqSagas,
} from './sagas/creationTool'
import {
	iVideoCreationOeqSagas,
	iVideoCreationMcqSagas,
} from './sagas/interactiveVideoCreation'
import errorCodes from 'helpers/enums/errorCodes'
import onboardingGuideSagas from './sagas/onboarding_guide_sagas'
import activityBanksSagas from './sagas/activity_banks_sagas'
import chameleonSagas from './sagas/chameleon_sagas'
import whiteboardSagas from './sagas/whiteboard_sagas'

import config from 'config'
import bytebot from '../helpers/bytebot'

function *watchFetchShareData() {
	yield* takeEvery(t.FETCH_SHARE_DATA, fetchShareData)
}
function *fetchShareData(action) {
	try {
		const payload = yield call(fetchShareDataSync, action.entityUID, action.entityType, action.libraryID)

		yield put({
			type: t.SHARE_VIEW_DATA_LOADED,
			payload,
		})
	} catch (error) {
		yield put({
			type: t.SHARE_VIEW_DATA_FAILED,
		})
	}
}

function *watchSendSimpleForm() {
	yield* takeEvery(t.SEND_FORM_POST, sendSimpleForm)
}
function *sendSimpleForm(action) {
	try {
		yield call(sendFormSync, action.params)
	} catch (error) {
		// console.log(error)
	}
}

function *watchSendCodeForm() {
	yield* takeEvery(t.CODE_FORM_SEND_SUBMITTED_CODE, sendSubmittedCode)
}
function *sendSubmittedCode(action) {
	try {
		let payload, successType, errorType
		switch (action.uid) {
			case 'join':
				payload = yield call(loginSync.validatePin, action.value)
				successType = t.JOIN_SUCCESS
				errorType = t.JOIN_ERROR
				break
			case 'signupReferral':
				{
					payload = yield call(loginSync.validateReferral, action.value)
					successType = t.SIGNUP_REFERRAL_SUCCESS
					errorType = t.SIGNUP_REFERRAL_ERROR
				}
				break
		}

		if (payload.valid) {
			action.resolve()
			yield put({
				type: successType,
				uid: action.uid,
				value: action.value,
			})
		} else {
			action.reject({ codeField: payload.message })
			yield put({
				type: errorType,
				uid: action.uid,
				value: action.value,
				message: payload.message,
			})
		}
	} catch (error) {
		action.reject({ _error: 'Something went wrong, please try again later' })
	}
}

function *watchResetCodeForm() {
	yield* takeEvery(t.CODE_FORM_RESET, resetCodeForm)
}
function *resetCodeForm(action) {
	switch (action.uid) {
		case 'signupReferral':
			yield put({
				type: t.SIGNUP_REFERRAL_RESET,
			})
			break
	}
}

function *watchGetSignupPasswordConditions() {
	yield* takeEvery(t.SIGNUP_GET_PASSWORD_CONDITIONS, getSignupPasswordConditions)
}
function *getSignupPasswordConditions(action) {
	const payload = yield call(loginSync.getPasswordConditions, action.recoveryKey)
	yield put({
		type: t.SIGNUP_SET_PASSWORD_CONDITIONS,
		conditions: payload,
	})
}

function *watchSendEmailSignupForm() {
	yield* takeEvery(t.SIGNUP_SEND_EMAIL_FORM, sendEmailSignupForm)
}
function *sendEmailSignupForm(action) {
	const sendEmailFormData = yield select(signupSelectors.sendEmailForm)
	const response = yield call(loginSync.sendEmailSignupForm, action.values, sendEmailFormData.isTeacher, sendEmailFormData.obLessonData)
	if (response.error_code !== 0) {
		if (response.payload.errors.possibleStudent !== undefined) {
			yield put(signupActions.confirmStudent())
			const confirmAction = yield take([t.SIGNUP_STUDENT_CONFIRMED, t.SIGNUP_TEACHER_CONFIRMED, t.CLOSE_CONFIRM])
			if (confirmAction.type === t.SIGNUP_TEACHER_CONFIRMED) {
				const errors = {}
				for (const key of Object.keys(response.payload.errors)) {
					if (key !== 'possibleStudent') {
						errors[key] = response.payload.errors[key]['errorText']
					}
				}
				errors['recaptcha'] = localize('signUp_form_recaptcha_error')
				action.reject(errors)
				yield put({
					type: t.SIGNUP_IS_TEACHER_CONFIRMATION,
				})
			} else if (confirmAction.type === t.SIGNUP_STUDENT_CONFIRMED) {
				window.location.href = '/student'
			} else {
				const errors = {}
				errors['recaptcha'] = localize('signUp_form_recaptcha_error')
				action.reject(errors)
			}
		} else {
			if (response.payload.errors.email
				&& response.payload.errors.email.errorCode === errorCodes.SIGNUP_EXISTING_EMAIL) {
				yield put(signupActions.teacherEmailAlreadyRegistered(action.values.email))
			} else {
				const errors = {}
				for (const key of Object.keys(response.payload.errors)) {
					errors[key] = response.payload.errors[key]['errorText']
				}
				action.reject(errors)
			}
		}
	} else {
		if (sendEmailFormData.obLessonData) {
			yield call(downloadObLesson)
		}
		yield put({
			type: t.SIGNUP_SEND_EMAIL_FORM_SUCCESS,
			data: response.payload,
			userEmail: action.values.email,
		})
	}
}

function *watchSendSsoSignupForm() {
	yield* takeEvery(t.SIGNUP_SEND_SSO_FORM, sendSsoSignupForm)
}
function *sendSsoSignupForm(action) {
	const sendEmailFormData = yield select(signupSelectors.sendEmailForm)
	const payload = yield call(loginSync.sendSsoSignupForm, action.values, sendEmailFormData.obLessonData)
	if (!payload.valid) {
		action.reject({
			_error: payload.message,
		})
	} else {
		if (sendEmailFormData.obLessonData) {
			yield call(downloadObLesson)
		}
		yield put({
			type: t.SIGNUP_SEND_SSO_FORM_SUCCESS,
			data: payload.data,
		})
	}
}

function *downloadObLesson() {
	try {
		const sendEmailFormData = yield select(signupSelectors.sendEmailForm)
		const response = yield callAny(cartSync.downloadProduct, sendEmailFormData.obLessonData.obEntityUID, sendEmailFormData.obLessonData.obEntityType)


		if (response && response.payload && response.payload.metrics) {
			yield call(mixpanel.track, 'Onboarding Lesson Saved', response.payload.metrics, true)
		}
	} catch (error) {
	}
}

function *watchSendLoginForm() {
	yield* takeEvery(t.LOGIN_SEND_FORM, sendLoginForm)
}
function *sendLoginForm(action) {
	try {
		const urlLoginParams = yield select(qsSelectors.qsLoginParamsSelector)
		const response = yield call(loginSync.sendLoginForm, action.values, urlLoginParams.fromLTI, urlLoginParams.referer)
		if (response.error_code === 0) {
			yield put({
				type: t.USER_IS_LOGGED,
				data: response.payload,
			})
		} else if (response.error_code === 2) {
			yield put({
				type: t.LOGIN_SAVE_RECOVERY_KEY,
				data: response.payload,
			})
		} else {
			yield put({
				type: t.LOGIN_AUTHENTICATION_FAILED,
			})
			action.reject({
				_error: response.message,
				password: response.message,
				email: '   ',
				/*do not delete the email error, otherwise the field won't appear red*/
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *watchAutoLogin() {
	yield* takeEvery([
		t.AUTO_LOGIN,
		t.LOGIN_INITIALIZE,
		t.STOREFRONT_VISITOR_INITIALIZE,
		t.STOREFRONT_INITIALIZE_PREVIEW,
		t.STORE_CONNECTOR_SEARCH_INITIALIZE,
		t.STORE_BASE_ROUTE_INITIALIZE,
		t.TEACHER_PRIVATE_LIBRARY_ROUTE_INITIALIZE,
		t.MAIN_SIDEBAR_CONNECTOR_INITIALIZE,
		t.STUDENT_INITIALIZE_LEAD,
	], autoLogin)
}
function *autoLogin() {
	const payload = yield call(loginSync.autoLogin)
	if (payload.logged) {
		yield put({
			type: t.USER_IS_AUTOLOGGED,
			data: payload.data,
		})
	} else {
		if (payload.metrics && payload.metrics.reset) {
			mixpanel.reset()
		}
		yield put({
			type: t.USER_IS_NOT_LOGGED,
			error_code: payload.error_code,
		})
	}
}

function *watchSendForgotPasswordForm() {
	yield* takeEvery(t.LOGIN_SEND_FORGOT_PASSWORD_FORM, sendForgotPasswordForm)
}
function *sendForgotPasswordForm(action) {
	const response = yield call(loginSync.sendForgotPasswordForm, action.values)
	if (response.error_code === 0) {
		browserHistory.push('/login' + location.search)
		yield put({
			type: t.OPEN_TOP_INFORMATION,
			messageObject: {
				message: response.message,
				styleType: 'success',
			},
		})
		action.resolve()
	} else {
		const errors = {}
		for (const key of Object.keys(response.payload.errors)) {
			errors[key] = response.payload.errors[key]['errorText']
		}
		action.reject(errors)
	}
}

function *watchValidateRecoveryKey() {
	yield* takeEvery(t.LOGIN_VALIDATE_RECOVERY_KEY, validateRecoveryKey)
}
function *validateRecoveryKey(action) {
	const payload = yield call(loginSync.validateRecoveryKey, action.key)
	if (payload.valid) {
		yield put({
			type: t.LOGIN_VALID_RECOVERY_KEY,
			key: action.key,
			mustAcceptTerms: payload.mustAcceptTerms,
		})
	} else {
		browserHistory.push('/login')
		yield put({
			type: t.OPEN_TOP_INFORMATION,
			messageObject: {
				message: payload.message,
				styleType: 'error',
			},
		})
	}
}

function *watchSendNewPasswordForm() {
	yield* takeEvery(t.LOGIN_SEND_NEW_PASSWORD_FORM, sendNewPasswordForm)
}
function *sendNewPasswordForm(action) {
	const payload = yield call(loginSync.sendNewPasswordForm, action.values)
	if (payload.valid) {
		yield put({
			type: t.OPEN_TOP_INFORMATION,
			messageObject: {
				message: payload.message,
				styleType: 'success',
				afterCallback: () => {
					window.location.href = payload.redirectUrl
				},
			},
		})
	} else {
		action.reject({
			errorCode: payload.errorCode,
			_error: payload.message,
		})
	}
}

function *watchSsoPopupRedirect() {
	yield* takeEvery(t.LOGIN_SSO_REDIRECT, ssoPopupRedirect)
}
function *ssoPopupRedirect(action) {
	if (action.isInIframe) {
		let win = window.open(
			`${action.redirectUrl}&fromLTI=${action.fromLTI}&sso_referer=/sso-successful-login/`,
			'nearpod_sso_login_popup',
			'width=800&height=800'
		)
		while (1) {
			const ssoLocation = checkSsoLocation(win)
			if (ssoLocation === 'closed') {
				win = null
				break
			} else if (ssoLocation === 'redirect') {
				const data = {redirectUrl: win.document.URL}
				win.close()
				win = null
				yield put({
					type: t.USER_IS_AUTOLOGGED,
					data,
				})
				break
			} else if (ssoLocation === 'success') {
				win.close()
				win = null
				yield call(autoLogin)
				break
			}
			yield call(delay, 2000)
		}
	}
}

function *tryBytebotLogin() {
	try {
		const urlParams = yield select(qsSelectors.qsBytebotParamsSelector)

		let google_token = ''
		// sk is a parameter that returns from the google sso
		// that represents a unique identifier for the user in google
		if (urlParams.sk) {
			const response = yield callAny(loginSync.getUserToken, urlParams.sk)
			// this token is the google access token for this sk user
			google_token = response.payload.access_token
		}
		// we retrieve wich type of license this user has in byteboot
		const bytebot_license_id = yield call(bytebot, google_token)

		if (google_token) { // user just registered from google sso
			// we register this user in nearpod and associate with bytebot
			const payload = yield call(
				loginSync.sendSsoSignupForm,
				{
					sk: urlParams.sk,
					isBytebot: true,
				}
			)
			if (!payload.valid) {
				throw new Error('Invalid payload')
			}
		} else { // in any other case we try to log in with bytebot
			// this service if this bytebot license id exists in nearpod log in the user
			// associated with it. if not it creates a user on the fly and logs in
			yield callAny(loginSync.loginViaBytebot, bytebot_license_id + urlParams.lcsbt)
		}

		// if all the above succeds and there is any throw or rejected promise
		// the user should be logged in
		yield put({
			type: t.USER_IS_LOGGED,
			data: {
				redirectUrl: config.app + 'library',
			},
		})
	} catch (error) {
		// console.log(error)
	}

}

export default function *rootSaga() {
	yield [
		binders,
		users,
		cart,
		myAccount,
		privateLibrary,
		folders,
		onboarding,
		adminOnboarding,
		themes,
		mediaSearch,
		metrics,
		quiz,
		matchingPairsCt,
		storefront,
		drawIt,
		processing,
		poll,
		oeq,
		desmos,
		flocabulary,
		notification,
		myLibrary,
		store,
		lti,
		game,
		student,
		firstLesson,
		googleSlidesSagas,
		onboardingActivitySagas,
		dmStorageExperiment,
		immersiveReaderSagas,
		goalsSagas,
		quickLaunchSagas,
		interactiveVideoSagas,
		navigationMenuSagas,
		userSchoolProfileSagas,
		flipgridSagas,
		interactiveVideoCreationSagas,
		fillInTheBlanksSession,
		iVideoCreationOeqSagas,
		creationToolOeqSagas,
		mediaSlideSagas,
		lessonSessionSagas,
		iVideoCreationMcqSagas,
		creationToolMcqSagas,
		activitiesSagas,
		teacherSagas,
		onboardingGuideSagas,
		activityBanksSagas,
		chameleonSagas,
		whiteboardSagas,
		watchFetchShareData(),
		watchSendSimpleForm(),
		watchSendCodeForm(),
		watchResetCodeForm(),
		watchGetSignupPasswordConditions(),
		watchSendEmailSignupForm(),
		watchSendSsoSignupForm(),
		watchSendLoginForm(),
		watchAutoLogin(),
		watchSendForgotPasswordForm(),
		watchValidateRecoveryKey(),
		watchSendNewPasswordForm(),
		watchSsoPopupRedirect(),
		takeEvery([t.LOGIN_INITIALIZE, t.SIGNUP_INITIALIZE], tryBytebotLogin),
	]
}
